import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Progressive resistance past a boundary, so the panel slows instead of
 * stopping dead when dragged past fully-open. See apple-design skill, §9.
 * @param {number} overshoot - Signed px past the boundary.
 * @param {number} dimension - The panel's width, used to scale the resistance.
 * @param {number} [constant]
 */
function rubberband(overshoot, dimension, constant = 0.55) {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/**
 * Minimal critically-damped spring integrator (semi-implicit Euler) for the
 * snap-back-to-open case. No external spring library is bundled by this
 * theme, so this stays small and self-contained rather than adding a new
 * runtime dependency for one interaction.
 *
 * @param {object} options
 * @param {number} options.from - Starting px offset.
 * @param {number} options.velocity - Initial px/s, carried over from the gesture.
 * @param {(x: number) => void} options.onUpdate
 * @param {() => void} options.onComplete
 * @param {number} [options.dampingRatio]
 * @param {number} [options.response] - Seconds; lower = snappier. Not a fixed duration.
 */
function springTo({ from, velocity, onUpdate, onComplete, dampingRatio = 1, response = 0.32 }) {
  const stiffness = (2 * Math.PI) / response;
  const stiffnessSq = stiffness * stiffness;
  const damping = 2 * dampingRatio * stiffness;
  let position = from;
  let v = velocity;
  let raf = 0;
  let lastTime = performance.now();

  function step(now) {
    const dt = Math.min((now - lastTime) / 1000, 1 / 30);
    lastTime = now;

    const acceleration = -stiffnessSq * position - damping * v;
    v += acceleration * dt;
    position += v * dt;

    if (Math.abs(position) < 0.5 && Math.abs(v) < 20) {
      onUpdate(0);
      onComplete();
      return;
    }

    onUpdate(position);
    raf = requestAnimationFrame(step);
  }

  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Drag-to-dismiss state, or null when no gesture is in progress. @type {null | {
   *   confirmed: boolean,
   *   startX: number,
   *   startY: number,
   *   offset: number,
   *   width: number,
   *   pointerId: number,
   *   history: { x: number, t: number }[],
   *   cancelSpring: (() => void) | null,
   * }} */
  #drag = null;

  /** @type {number} */
  #dragHysteresis = 10;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.addEventListener('pointerdown', this.#handleDragStart);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('pointerdown', this.#handleDragStart);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /** @param {PointerEvent} event */
  #handleDragStart = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open || event.button !== 0) return;

    // Don't hijack clicks/scrolls on buttons, links, inputs, or quantity steppers.
    if (/** @type {Element} */ (event.target).closest('button, a, input, select, textarea')) return;

    this.#drag?.cancelSpring?.();
    this.#drag = {
      confirmed: false,
      startX: event.clientX,
      startY: event.clientY,
      offset: 0,
      width: dialog.getBoundingClientRect().width,
      pointerId: event.pointerId,
      history: [{ x: event.clientX, t: event.timeStamp }],
      cancelSpring: null,
    };

    this.addEventListener('pointermove', this.#handleDragMove);
    this.addEventListener('pointerup', this.#handleDragEnd);
    this.addEventListener('pointercancel', this.#handleDragEnd);
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    const drag = this.#drag;
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!drag || !dialog || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (!drag.confirmed) {
      // Detect intent in parallel; the first axis to clear the threshold wins
      // (apple-design §10). Vertical intent bails out and lets native scroll run.
      if (Math.abs(dy) > this.#dragHysteresis && Math.abs(dy) > Math.abs(dx)) {
        this.#endDrag();
        return;
      }
      if (Math.abs(dx) <= this.#dragHysteresis || Math.abs(dx) < Math.abs(dy)) return;

      drag.confirmed = true;
      // Wrapped defensively: some browsers can reject this for a pointerId
      // whose capture state changed between events; the drag still works
      // via plain event listeners without capture, just without the
      // guarantee of continued delivery outside the element's bounds.
      try {
        dialog.setPointerCapture(drag.pointerId);
      } catch {}
      // Kill the forwards-filled open animation so an inline transform can
      // take over — CSS Animations otherwise keep priority over it.
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
    }

    event.preventDefault();

    // 1:1 toward closed; rubber-band resistance past fully-open (§9).
    drag.offset = dx >= 0 ? Math.min(dx, drag.width) : -rubberband(-dx, drag.width);
    dialog.style.transform = `translateX(${drag.offset}px)`;

    drag.history.push({ x: event.clientX, t: event.timeStamp });
    if (drag.history.length > 6) drag.history.shift();
  };

  /** @param {PointerEvent} event */
  #handleDragEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.removeEventListener('pointermove', this.#handleDragMove);
    this.removeEventListener('pointerup', this.#handleDragEnd);
    this.removeEventListener('pointercancel', this.#handleDragEnd);

    if (!drag.confirmed) {
      this.#drag = null;
      return;
    }

    const { dialog } = /** @type {Refs} */ (this.refs);
    const oldest = drag.history[0];
    const newest = drag.history[drag.history.length - 1];
    const dt = (newest.t - oldest.t) / 1000;
    const velocity = dt > 0 ? (newest.x - oldest.x) / dt : 0; // px/s

    const commitClose = drag.offset > drag.width * 0.35 || velocity > 500;

    if (commitClose) {
      // Hand off to the CSS close animation, but let it continue from the
      // live drag position instead of snapping back to 0 first (§3, §5).
      dialog.style.setProperty('--drag-start-x', `${drag.offset}px`);
      dialog.style.transform = '';
      // closeDialog() restarts the animation by toggling 'none' -> '' across
      // a reflow; if it's already 'none' (left over from drag-confirm) that
      // toggle is a no-op and the close animation never fires. Clear it first.
      dialog.style.animation = '';
      this.#drag = null;
      this.closeDialog().then(() => dialog.style.removeProperty('--drag-start-x'));
      return;
    }

    // Snap back to open, carrying the release velocity through so there's
    // no seam between drag and animation (§5, §6).
    drag.cancelSpring = springTo({
      from: drag.offset,
      velocity,
      dampingRatio: 1,
      response: 0.32,
      onUpdate: (x) => {
        dialog.style.transform = x === 0 ? '' : `translateX(${x}px)`;
      },
      onComplete: () => {
        // Leave animation: 'none' in place rather than clearing it — clearing
        // it would let the stylesheet's [open] (opening) keyframe re-match
        // and replay the slide-in from scratch, flashing the panel.
        this.#drag = null;
      },
    });
  };

  #endDrag() {
    const drag = this.#drag;
    if (!drag) return;
    this.removeEventListener('pointermove', this.#handleDragMove);
    this.removeEventListener('pointerup', this.#handleDragEnd);
    this.removeEventListener('pointercancel', this.#handleDragEnd);
    this.#drag = null;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
